// src/features/treinos/components/treino-screens/exerciseLibraryService.js
//
// Acesso SOMENTE-LEITURA à exercise_library (Supabase). É a MESMA fonte do
// gerador IA — o usuário só escolhe o exercício; muscle_group e
// secondary_muscle_groups vêm materializados da lib (nunca digitados).
//
// Schema (confirmado via REST):
//   id, name, name_regional, muscle_group, secondary_muscle_groups[],
//   video_url_1, video_url_2, thumbnail_url, category, is_global
// muscle_group ∈ {Costas, Peito, Abdômen, Inferiores, Bíceps, Panturrilha,
//   Aeróbio, Ombros, Funcional, Tríceps, Mobilidade}

import { supabase } from "../../../../supabaseClient.js";

// Grupos canônicos da lib (ordem de exibição nos chips).
export const MUSCLE_GROUPS = [
  "Peito", "Costas", "Ombros", "Tríceps", "Bíceps",
  "Inferiores", "Abdômen", "Panturrilha", "Funcional", "Aeróbio", "Mobilidade",
];

// Movimentos fundamentais/mais usados, em ordem editorial. Termos parciais
// mantem a recomendacao compativel com variacoes regionais dos nomes da base.
const RECOMMENDED_TERMS = {
  Peito: ["supino reto", "supino inclinado", "supino declinado", "crucifixo", "crossover", "flexao", "peck deck", "voador", "paralela", "pullover"],
  Costas: ["puxada frente", "puxada alta", "barra fixa", "remada curvada", "remada baixa", "remada unilateral", "serrote", "pulldown", "levantamento terra", "pullover"],
  Ombros: ["desenvolvimento", "elevacao lateral", "elevacao frontal", "crucifixo inverso", "face pull", "remada alta", "arnold", "encolhimento"],
  "Triceps": ["triceps pulley", "triceps corda", "triceps testa", "triceps frances", "triceps banco", "triceps coice", "supino fechado", "paralela"],
  "Biceps": ["rosca direta", "rosca alternada", "rosca martelo", "rosca scott", "rosca concentrada", "rosca inclinada", "rosca inversa", "barra fixa"],
  Inferiores: ["agachamento livre", "leg press", "levantamento terra", "stiff", "cadeira extensora", "mesa flexora", "cadeira flexora", "afundo", "passada", "elevacao pelvica", "hip thrust", "agachamento bulgaro", "cadeira abdutora", "cadeira adutora"],
  "Abdomen": ["abdominal supra", "abdominal infra", "prancha", "elevacao de pernas", "abdominal bicicleta", "abdominal obliquo", "crunch", "russian twist", "canivete", "dead bug"],
  Panturrilha: ["panturrilha em pe", "panturrilha sentado", "panturrilha no leg press", "elevacao de panturrilha", "gemeos"],
  Funcional: ["burpee", "kettlebell swing", "agachamento", "mountain climber", "polichinelo", "corda naval", "farmer", "box jump", "bear crawl", "thruster"],
  "Aerobio": ["corrida", "caminhada", "bicicleta", "eliptico", "remo", "corda", "escada", "esteira", "spinning"],
  Mobilidade: ["mobilidade de quadril", "mobilidade de ombro", "mobilidade toracica", "alongamento", "rotacao", "cat cow", "tornozelo"],
};

const normalizeRecommendationText = (value = "") =>
  String(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export function getRecommendedExercises(rows = [], group = null, limit = 15) {
  if (!group || !Array.isArray(rows) || rows.length === 0) return [];
  const terms = (RECOMMENDED_TERMS[normalizeRecommendationText(group).replace(/^./, (c) => c.toUpperCase())] || [])
    .map(normalizeRecommendationText);

  return rows
    .map((exercise, originalIndex) => {
      const searchable = normalizeRecommendationText(`${exercise?.name || ""} ${exercise?.name_regional || ""}`);
      const termIndex = terms.findIndex((term) => searchable.includes(term));
      return { exercise, originalIndex, rank: termIndex < 0 ? Number.MAX_SAFE_INTEGER : termIndex };
    })
    .sort((a, b) => a.rank - b.rank || a.originalIndex - b.originalIndex)
    .slice(0, limit)
    .map(({ exercise }) => exercise);
}

const SELECT_COLS =
  "id,name,name_regional,muscle_group,secondary_muscle_groups,video_url_1,video_url_2,thumbnail_url";

/**
 * Busca exercícios na lib por grupo muscular e/ou texto.
 * @param {{ query?: string, group?: string|null, limit?: number }} params
 * @returns {Promise<Array>} linhas da exercise_library
 */
export async function searchExercises({ query = "", group = null, groups = [], limit = 60 } = {}) {
  let q = supabase
    .from("exercise_library")
    .select(SELECT_COLS)
    .order("name", { ascending: true })
    .limit(limit);

  if (group) q = q.eq("muscle_group", group);
  else if (groups.length) q = q.in("muscle_group", groups);

  const term = String(query || "").trim();
  if (term) {
    const safe = term.replace(/[%,()]/g, " ");
    q = q.or(`name.ilike.%${safe}%,name_regional.ilike.%${safe}%,muscle_group.ilike.%${safe}%`);
  }

  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

// Cache de módulo: id da exercise_library → { video_url, video_url_2, thumbnail_url }
// (ou null se o id não existe na lib). Evita refazer query entre telas.
const _mediaCache = new Map();

/**
 * Busca a MÍDIA (vídeo/thumbnail) de vários exercícios da exercise_library por id.
 * Os exercícios do plano/sessão guardam só EXERCISE_ID (= id da lib); a mídia mora
 * aqui no Supabase — é a MESMA fonte da Biblioteca. Resultado cacheado por id.
 * @param {Array<string>} ids ids de exercise_library (EXERCISE_ID)
 * @returns {Promise<Record<string, {video_url, video_url_2, thumbnail_url}|null>>}
 */
export async function fetchExerciseMediaByIds(ids = []) {
  const unique = [...new Set((ids || []).filter(Boolean))];
  const missing = unique.filter((id) => !_mediaCache.has(id));

  if (missing.length) {
    const { data, error } = await supabase
      .from("exercise_library")
      .select("id,video_url_1,video_url_2,thumbnail_url")
      .in("id", missing);

    if (!error && Array.isArray(data)) {
      for (const r of data) {
        _mediaCache.set(r.id, {
          video_url: r.video_url_1 || null,
          video_url_2: r.video_url_2 || null,
          thumbnail_url: r.thumbnail_url || null,
        });
      }
    }
    // ids sem retorno → marca null p/ não reconsultar
    for (const id of missing) if (!_mediaCache.has(id)) _mediaCache.set(id, null);
  }

  const out = {};
  for (const id of unique) out[id] = _mediaCache.get(id) || null;
  return out;
}

// ─────────────────────────────────────────────────────────────────────────────
// Índice GLOBAL de mídia (id + nome → vídeo/thumbnail). Carregado UMA vez e
// cacheado. Permite resolver a mídia por EXERCISE_ID OU por NOME — exercícios de
// plano de IA muitas vezes não trazem EXERCISE_ID, então o fallback por nome é o
// que faz o vídeo aparecer igual à Biblioteca em todo lugar.
// ─────────────────────────────────────────────────────────────────────────────
const _byId = new Map();
const _byName = new Map();
let _indexPromise = null;

const _normName = (s = "") =>
  String(s).normalize("NFD").replace(/\p{Diacritic}/gu, "").trim().toLowerCase();

/** Carrega (1x) o índice de mídia da exercise_library. Idempotente. */
export function loadExerciseMediaIndex() {
  if (!_indexPromise) {
    _indexPromise = supabase
      .from("exercise_library")
      .select("id,name,name_regional,video_url_1,video_url_2,thumbnail_url")
      .limit(2000)
      .then(({ data, error }) => {
        if (error || !Array.isArray(data)) {
          _indexPromise = null; // permite retry numa próxima montagem
          return;
        }
        for (const r of data) {
          const media = {
            video_url: r.video_url_1 || null,
            video_url_2: r.video_url_2 || null,
            thumbnail_url: r.thumbnail_url || null,
          };
          if (r.id != null) _byId.set(String(r.id), media);
          if (r.name) _byName.set(_normName(r.name), media);
          if (r.name_regional) _byName.set(_normName(r.name_regional), media);
        }
      })
      .catch(() => { _indexPromise = null; });
  }
  return _indexPromise;
}

/** Resolve mídia de um exercício pelo índice: por EXERCISE_ID, senão por NOME. */
export function resolveExerciseMedia(ex) {
  if (!ex) return null;
  const id = ex.EXERCISE_ID ?? ex.exercise_id ?? ex.exercise_library?.id ?? ex.id;
  if (id != null && _byId.has(String(id))) return _byId.get(String(id));
  const nm = _normName(ex.nome ?? ex.NOME ?? ex.name ?? ex.exercise_library?.name);
  if (nm && _byName.has(nm)) return _byName.get(nm);
  return null;
}

/**
 * Converte uma linha da lib + parâmetros do usuário no shape de exercício que
 * as telas (planoTreinoData / MuscleMap / workoutXp) leem.
 * muscle_group e secondary_muscle_groups vêm SEMPRE da lib.
 * @param {Object} lib linha da exercise_library
 * @param {{ series?, reps?, carga?, descanso? }} params
 */
export function libToExercicio(lib, params = {}) {
  return {
    EXERCISE_ID: lib.id || null,
    nome: lib.name || "Exercício",
    muscle_group: lib.muscle_group || null,            // primário (da lib)
    secondary_muscle_groups: Array.isArray(lib.secondary_muscle_groups)
      ? lib.secondary_muscle_groups
      : [],                                            // secundários (da lib)
    series: params.series != null && params.series !== "" ? Number(params.series) : null,
    reps: params.reps ?? null,
    carga: params.carga ?? null,
    descanso: params.descanso ?? null,
    video_url: lib.video_url_1 || null,
    video_url_2: lib.video_url_2 || null,
    thumbnail_url: lib.thumbnail_url || null,
  };
}
